/**
 * D-175, shared by the four exams' grading tabs: the AI corrects everything, the trainer re-marks
 * what they want. The warnings of the back office, the filters of the list of corrected work, and
 * the AI's correction shown in the sheet.
 */

import { useAcademyProductionsOverviewRetrieve } from "@agencyos/api-client";
import { Badge, SelectField, TextField } from "@agencyos/ui-web";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAdminExam } from "./examContext";

export type GradingQueue = "graded" | "submitted" | "to_trainer";
export const GRADING_QUEUES: GradingQueue[] = ["graded", "submitted", "to_trainer"];

export interface GradingFilterValues {
  status: GradingQueue;
  skill?: string;
  source?: string;
  attention?: string;
  learner?: string;
  date_from?: string;
  date_to?: string;
}

export function useGradingFilters() {
  const [values, setValues] = useState({ status: "graded" as GradingQueue, skill: "", source: "", attention: "", learner: "", date_from: "", date_to: "" });
  const params = useMemo<GradingFilterValues>(() => {
    const out: GradingFilterValues = { status: values.status };
    for (const key of ["skill", "source", "attention", "learner", "date_from", "date_to"] as const) {
      if (values[key]) out[key] = values[key];
    }
    return out;
  }, [values]);
  return { values, setValues, params };
}

export function GradingFilters({ values, onChange, course }: { values: ReturnType<typeof useGradingFilters>["values"]; onChange: (next: ReturnType<typeof useGradingFilters>["values"]) => void; course?: boolean }) {
  const { t } = useTranslation();
  const set = (key: keyof typeof values, value: string) => onChange({ ...values, [key]: value });
  return (
    <div className="form-grid">
      <SelectField
        label={t("academyAdmin.grading.queue")}
        value={values.status}
        options={GRADING_QUEUES.map((q) => ({ value: q, label: t(`academyAdmin.grading.queues.${q}`) }))}
        onChange={(e) => set("status", e.target.value)}
      />
      <SelectField
        label={t("academyAdmin.grading.filters.skill")}
        value={values.skill}
        options={[
          { value: "", label: t("academyAdmin.grading.filters.all") },
          { value: "ee", label: "EE" },
          { value: "eo", label: "EO" },
        ]}
        onChange={(e) => set("skill", e.target.value)}
      />
      <SelectField
        label={t("academyAdmin.grading.filters.source")}
        value={values.source}
        options={[
          { value: "", label: t("academyAdmin.grading.filters.all") },
          { value: "ai", label: t("academyAdmin.grading.byAi") },
          { value: "trainer", label: t("academyAdmin.grading.byTrainer") },
        ]}
        onChange={(e) => set("source", e.target.value)}
      />
      <SelectField
        label={t("academyAdmin.grading.filters.attention")}
        value={values.attention}
        options={[
          { value: "", label: t("academyAdmin.grading.filters.all") },
          { value: "listen", label: t("academyAdmin.grading.toListen") },
          ...(course ? [] : [{ value: "review", label: t("academyAdmin.grading.reviewAsked") }]),
        ]}
        onChange={(e) => set("attention", e.target.value)}
      />
      <TextField label={t("academyAdmin.grading.filters.learner")} value={values.learner} onChange={(e) => set("learner", e.target.value)} />
      <TextField type="date" label={t("academyAdmin.grading.filters.from")} value={values.date_from} onChange={(e) => set("date_from", e.target.value)} />
      <TextField type="date" label={t("academyAdmin.grading.filters.to")} value={values.date_to} onChange={(e) => set("date_to", e.target.value)} />
    </div>
  );
}

/** No AI configured, work waiting, past the bound, recordings to listen to. */
export function GradingWarnings() {
  const { t } = useTranslation();
  const { params } = useAdminExam();
  const query = useAcademyProductionsOverviewRetrieve(params);
  const data = query.data?.status === 200 ? query.data.data : undefined;
  if (!data) return null;
  return (
    <>
      {!data.ai_configured && (
        <p className="notice-warning installment-row" role="alert">
          {t("academyAdmin.grading.noAi", { count: data.waiting })}
        </p>
      )}
      {data.waiting_too_long > 0 && (
        <p className="notice-warning installment-row" role="alert">
          {t("academyAdmin.grading.tooLong", { count: data.waiting_too_long, days: data.max_days })}
        </p>
      )}
      {data.to_listen > 0 && <p className="muted">{t("academyAdmin.grading.toListenCount", { count: data.to_listen })}</p>}
    </>
  );
}

/** Tags of a row of the list. */
export function GradingTags({ row }: { row: { needs_listening?: boolean; review_pending?: boolean; waiting_too_long?: boolean } }) {
  const { t } = useTranslation();
  return (
    <>
      {row.needs_listening ? <Badge tone="danger">{t("academyAdmin.grading.toListen")}</Badge> : null}
      {row.review_pending ? <Badge tone="warning">{t("academyAdmin.grading.reviewAsked")}</Badge> : null}
      {row.waiting_too_long ? <Badge tone="danger">{t("academyAdmin.grading.waitingTooLong")}</Badge> : null}
    </>
  );
}

function isRow(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/** What the AI said, under the recording and the transcript: the trainer corrects with it in view. */
export function AiCorrection({ grade }: { grade: unknown }) {
  const { t } = useTranslation();
  if (!isRow(grade)) return null;
  const errors = (Array.isArray(grade.errors) ? grade.errors : []).filter(isRow);
  const advice = (Array.isArray(grade.advice) ? grade.advice : []).filter((a): a is string => typeof a === "string");
  const check = isRow(grade.audio_check) ? grade.audio_check : null;
  const corrected = typeof grade.corrected_version === "string" ? grade.corrected_version : "";
  if (!errors.length && !advice.length && !check && !corrected && grade.unintelligible !== true) return null;
  return (
    <section className="installment-row stack">
      <strong>{t("academyAdmin.grading.aiCorrection")}</strong>
      {grade.unintelligible === true && <p className="ko">{t("academyAdmin.grading.unintelligible")}</p>}
      {check && (
        <p className="muted ao-num">
          {t("academyAdmin.grading.audioCheck", { seconds: String(check.seconds ?? "—"), peak: String(check.peak_db ?? "—"), rms: String(check.rms_db ?? "—") })}
        </p>
      )}
      {errors.length > 0 && (
        <ul>
          {errors.map((row, index) => (
            <li key={index}>
              {String(row.excerpt ?? "")} → {String(row.correction ?? "")}
              {row.explanation ? <span className="muted"> · {String(row.explanation)}</span> : null}
            </li>
          ))}
        </ul>
      )}
      {corrected && <p className="academy-pre">{corrected}</p>}
      {advice.length > 0 && (
        <ul>
          {advice.map((line, index) => (
            <li key={index}>{line}</li>
          ))}
        </ul>
      )}
    </section>
  );
}
